import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, Chip, IconButton, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, Tooltip, TablePagination,
  Checkbox, Alert, Switch,
} from '@mui/material';
import { Search, Add, Edit, Delete, Refresh, Visibility, School, Badge, Download } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import { useNavigate } from 'react-router-dom';
import AsyncStorage from '../../services/AsyncStorage';

const emptyForm = {
  fullName: '', phone: '', pass_plane: '', rollNumber: '', email: '', gender: 'Male',
  standard: '', section: '', academicYear: '',
  fatherName: '', motherName: '', fatherPhone: '', motherPhone: '',
  dob: '', bloodGroup: '', currentAddress: '', permanentAddress: '',
  aadhar: '', remarks: '', status: 'Active', profile_pic_path: ''
};

export default function Students() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [filterClass, setFilterClass] = useState('All');
  const [filterAcYear, setFilterAcYear] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showInactive, setShowInactive] = useState(false);
  const [inactive, setInactive] = useState([]);

  // Bulk promote state
  const [selectedIds, setSelectedIds] = useState([]);
  const [promoteDialog, setPromoteDialog] = useState(false);
  const [promoteForm, setPromoteForm] = useState({ academic_year: '', standard: '', section: '' });
  const [promoting, setPromoting] = useState(false);
  const [promoteResult, setPromoteResult] = useState(null);

  // ID Card bulk state
  const [idCardDialog, setIdCardDialog] = useState(false);
  const [idCardAcYear, setIdCardAcYear] = useState('');
  const [idCardLoading, setIdCardLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      const [secData, acData] = await Promise.all([
        InvokeApi(Config.main_url + apiList.getDeptartmentSection, { status: 'Active' }),
        InvokeApi(Config.main_url + apiList.getAvailableAcademicYears, {}),
      ]);
      setSections(secData?.sections || []);
      const years = acData?.academic_years || [];
      setAcademicYears(years);
      const current = years.find(y => y.is_current) || years[0];
      const acYear = current?.value || '';
      if (acYear) {
        setFilterAcYear(acYear);
        fetchStudents('All', acYear);
      }
    };
    init();
  }, [showInactive]);

  const fetchStudents = useCallback(async (classKey, acYear) => {
    setLoading(true);
    setPage(0);
    let list = [];
    let inactiveList = [];

    if (classKey === 'All') {
      const [activeData, inactiveData] = await Promise.all([
        InvokeApi(Config.main_url + apiList.getStudents, { status: 'Active' }),
        InvokeApi(Config.main_url + apiList.getStudents, { status: 'Inactive' }),
      ]);
      // const data = await InvokeApi(Config.main_url + apiList.getStudents, {});
      list = activeData?.students || [];
      inactiveList = inactiveData?.students || [];
    } else {
      const [std, sec] = classKey.split('||');
      const data = await InvokeApi(Config.main_url + apiList.getStudentsByStdSecAcYear, {
        standard: std,
        section: sec,
        acYear: acYear,
      });
      list = data?.students || [];
    }
    setStudents(list);
    setInactive(inactiveList || []);
    setLoading(false);
  }, []);

  const handleClassChange = (val) => {
    setFilterClass(val);
    fetchStudents(val, filterAcYear);
  };

  const handleAcYearChange = (val) => {
    setFilterAcYear(val);
    fetchStudents(filterClass, val);
  };

  const displayed = showInactive ? [...students, ...inactive] : students;

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      displayed.filter(s =>
        !q ||
        s.full_name?.toLowerCase().includes(q) ||
        String(s.ID).includes(q)
      )
    );
    setPage(0);
  }, [search, students]);

  const classOptions = sections.map(s => ({
    value: `${s.standard?.trim()}||${s.section?.trim()}`,
    label: `${s.standard?.trim()} - ${s.section?.trim()}`,
  }));

  const standards = [...new Set(sections.map(s => s.standard?.trim()).filter(Boolean))];
  const sectionsByStd = sections.filter(s => s.standard === form.standard).map(s => s.section);

  const openAdd = () => {
    const current = academicYears.find(y => y.is_current) || academicYears[0];
    const [std, sec] = filterClass !== 'All' ? filterClass.split('||') : ['', ''];
    setForm({ ...emptyForm, standard: std, section: sec, academicYear: filterAcYear || current?.value || '' });
    setEditMode(false);
    setDialog(true);
  };

  const openEdit = (s) => {
    setForm({
      studentId:        s.ID,
      fullName:         s.full_name         || '',
      phone:            s.phone             || '',
      rollNumber:       s.roll_number       || '',
      email:            s.email             || '',
      gender:           s.gender            || 'Male',
      standard:         s.standard          || '',
      section:          s.section           || '',
      academicYear:     s.academic_year     || '',
      fatherName:       s.father_name       || '',
      motherName:       s.mother_name       || '',
      guardianName:     s.guardian_name     || '',
      fatherPhone:      s.father_phone      || '',
      motherPhone:      s.mother_phone      || '',
      dob:              s.dob ? s.dob.split('T')[0] : '',
      bloodGroup:       s.blood_group       || '',
      currentAddress:   s.current_address   || '',
      permanentAddress: s.permanent_address || '',
      aadhar:           s.aadhar            || '',
      remarks:          s.remarks           || '',
      pass_plane:       s.pass_plane        || '',
      profile_pic_path: s.profile_pic_path  || '',
    });
    setEditMode(true);
    setDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this student?')) return;
    await InvokeApi(Config.main_url + apiList.deleteStudent, { id: id, status: 'Inactive' });
    fetchStudents(filterClass, filterAcYear);
  };

  const handleSave = async () => {
    if (!form.fullName || !form.phone || !form.standard || !form.academicYear) {
      alert('Full name, phone, standard and academic year are required');
      return;
    }
    setSaving(true);
    const url = editMode ? apiList.editStudent : apiList.addStudent;

    const payload = editMode ? {
      studentId:        form.studentId,
      full_name:        form.fullName,
      pass_plane:       form.pass_plane       ?? '',
      roll_number:      form.rollNumber       ?? '',
      standard:         form.standard,
      section:          form.section          ?? '',
      academic_year:    form.academicYear,
      aadhar:           form.aadhar           ?? '',
      gender:           form.gender           ?? 'Male',
      whatsapp:         form.phone            ?? '',   
      father_name:      form.fatherName       ?? '',
      mother_name:      form.motherName       ?? '',
      guardian_name:    form.guardianName     ?? '',
      father_phone:     form.fatherPhone      ?? '',
      mother_phone:     form.motherPhone      ?? '',
      email:            form.email            ?? '',
      dob:              form.dob              ?? '',
      blood_group:      form.bloodGroup       ?? '',
      profile_pic:      form.profile_pic_path ?? '',
      profile_pic_path: form.profile_pic_path ?? '',
      permanent_address: form.permanentAddress ?? '',
      current_address:  form.currentAddress   ?? '',
      remarks:          form.remarks          ?? '',
    } : form;   

    const data = await InvokeApi(Config.main_url + url, payload);
    setSaving(false);
    if (data?.code === '200') { setDialog(false); fetchStudents(filterClass, filterAcYear); }
    else alert(data?.message || 'Failed to save');
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleSelectAll = () => {
    const pageIds = paginated.map(s => s.ID);
    const allSelected = pageIds.every(id => selectedIds.includes(id));
    setSelectedIds(prev =>
      allSelected ? prev.filter(id => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])]
    );
  };

  const openPromoteDialog = () => {
    const current = academicYears.find(y => y.is_current) || academicYears[0];
    setPromoteForm({ academic_year: current?.value || '', standard: '', section: '' });
    setPromoteResult(null);
    setPromoteDialog(true);
  };

  const handleBulkPromote = async () => {
    if (!promoteForm.academic_year || !promoteForm.standard || !promoteForm.section) {
      alert('Academic year, standard and section are required');
      return;
    }
    setPromoting(true);
    setPromoteResult(null);
    const data = await InvokeApi(Config.main_url + apiList.bulkPromoteStudents, {
      student_ids: selectedIds,
      academic_year: promoteForm.academic_year,
      standard: promoteForm.standard,
      section: promoteForm.section,
    });
    setPromoting(false);
    if (data?.code === '200') {
      setPromoteResult(data);
    } else {
      alert(data?.message || 'Bulk promote failed');
    }
  };

  const closePromoteDialog = () => {
    setPromoteDialog(false);
    setPromoteResult(null);
    if (selectedIds.length > 0) {
      setSelectedIds([]);
      fetchStudents(filterClass, filterAcYear);
    }
  };

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openBulkIdCards = async (type) => {
    setIdCardLoading(true);
    const token = await AsyncStorage.getItem('token');
    let url = '';
    if (type === 'all') {
      url = `${Config.main_url}/generateIdCardBulkAll${idCardAcYear ? '?ac_year=' + idCardAcYear : ''}`;
    } else if (type === 'section' && filterClass !== 'All') {
      const [std, sec] = filterClass.split('||');
      url = `${Config.main_url}/generateIdCardBulk/${encodeURIComponent(std)}/${encodeURIComponent(sec)}${idCardAcYear ? '?ac_year=' + idCardAcYear : ''}`;
    } else {
      alert('Please select a specific class/section first for section-wise generation.');
      setIdCardLoading(false);
      return;
    }
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { alert('Failed to generate ID cards'); setIdCardLoading(false); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { alert('Error: ' + e.message); }
    setIdCardLoading(false);
    setIdCardDialog(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Students</Typography>
          <Typography variant="body2" color="text.secondary">{filtered.length} student{filtered.length !== 1 ? 's' : ''} found</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {/* Show Inactive toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid #e8eaf6', borderRadius: 2, px: 1.5, py: 0.5 }}>
            <Typography fontSize={13} fontWeight={600} color="text.secondary">Inactive</Typography>
            <Switch
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#1a237e' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1a237e' },
              }}
            />
          </Box>
          {selectedIds.length > 0 && (
            <Button variant="contained" startIcon={<School />} onClick={openPromoteDialog}
              sx={{ background: 'linear-gradient(135deg,#6a1b9a,#8e24aa)', borderRadius: 2, fontWeight: 700 }}>
              Promote ({selectedIds.length})
            </Button>
          )}
          <Button variant="outlined" startIcon={<Badge />} onClick={() => {
              const cur = academicYears.find(y => y.is_current) || academicYears[0];
              setIdCardAcYear(cur?.value || '');
              setIdCardDialog(true);
            }}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e', fontWeight: 700 }}>
            ID Cards
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add Student
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search ID, name..." value={search}
            onChange={e => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          <TextField select size="small" label="Class" value={filterClass} onChange={e => handleClassChange(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="All">All Classes</MenuItem>
            {classOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Academic Year" value={filterAcYear} onChange={e => handleAcYearChange(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All Years</MenuItem>
            {academicYears.map(y => (
              <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
            ))}
          </TextField>
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchStudents(filterClass, filterAcYear)} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}><Refresh /></IconButton>
          </Tooltip>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#1a237e' }} /></Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    <TableCell padding="checkbox" sx={{ pl: 2 }}>
                      <Checkbox
                        size="small"
                        indeterminate={paginated.some(s => selectedIds.includes(s.ID)) && !paginated.every(s => selectedIds.includes(s.ID))}
                        checked={paginated.length > 0 && paginated.every(s => selectedIds.includes(s.ID))}
                        onChange={toggleSelectAll}
                        sx={{ color: '#1a237e', '&.Mui-checked': { color: '#1a237e' }, '&.MuiCheckbox-indeterminate': { color: '#1a237e' } }}
                      />
                    </TableCell>
                    {['ID', 'Student', 'Standard', 'Section', 'Phone', 'Ac. Year', 'Status', 'Actions'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>No students found</TableCell></TableRow>
                  ) : paginated.map(s => (
                    <TableRow key={s.ID} hover selected={selectedIds.includes(s.ID)}
                      sx={{ '&.Mui-selected': { background: '#f3e5f5' }, '&.Mui-selected:hover': { background: '#ede7f6' } }}>
                      <TableCell padding="checkbox" sx={{ pl: 2 }}>
                        <Checkbox size="small" checked={selectedIds.includes(s.ID)} onChange={() => toggleSelect(s.ID)}
                          sx={{ color: '#6a1b9a', '&.Mui-checked': { color: '#6a1b9a' } }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={`#${s.ID}`} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 12 }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar src={s.profile_pic}
                            sx={{ width: 34, height: 34, background: 'linear-gradient(135deg,#1a237e,#1565c0)', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                            {s.full_name?.[0]}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600} fontSize={13}>{s.full_name}</Typography>
                            <Typography fontSize={11} color="text.secondary">{s.roll_number}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={s.standard} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600 }} /></TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{s.section}</TableCell>
                      {/* <TableCell sx={{ fontSize: 13 }}>{s.roll_number || '—'}</TableCell> */}
                      <TableCell sx={{ fontSize: 12 }}>{s.phone}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{s.academic_year || '—'}</TableCell>
                      <TableCell>
                        <Chip label={s.status || 'Active'} size="small"
                          sx={{ background: s.status === 'Active' ? '#e8f5e9' : '#ffebee', color: s.status === 'Active' ? '#2e7d32' : '#c62828', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => navigate(`/students/${s.ID}`)} sx={{ color: '#1a237e' }}><Visibility fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(s)} sx={{ color: '#1565c0' }}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(s.ID)} sx={{ color: '#c62828' }}><Delete fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={filtered.length} page={page}
              onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }} />
          </>
        )}
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', }}>
          {editMode ? `Edit Student #${form.studentId}` : 'Add New Student'}
        </DialogTitle>
        <DialogContent sx={{ pt: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Full Name *" value={form.fullName} onChange={f('fullName')} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Phone *" value={form.phone} onChange={f('phone')} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Password" value={form.pass_plane} onChange={f('pass_plane')} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Roll Number" value={form.rollNumber} onChange={f('rollNumber')} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Email" value={form.email} onChange={f('email')} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Gender *" value={form.gender} onChange={f('gender')}>
                {['Male', 'Female', 'Other'].map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Date of Birth" value={form.dob} onChange={f('dob')} InputLabelProps={{ shrink: true }} /></Grid>
          
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size="small" label="Standard *" value={form.standard} onChange={e => { f('standard')(e); setForm(p => ({ ...p, standard: e.target.value, section: '' })); }}>
                {standards.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size="small" label="Section" value={form.section} onChange={f('section')}>
                {sectionsByStd.length > 0
                  ? sectionsByStd.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)
                  : <MenuItem value={form.section || 'A'}>{form.section || 'A'}</MenuItem>
                }
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size="small" label="Academic Year *" value={form.academicYear} onChange={f('academicYear')}>
                {academicYears.map(y => (
                  <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Father Name" value={form.fatherName} onChange={f('fatherName')} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Father Phone" value={form.fatherPhone} onChange={f('fatherPhone')} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Mother Name" value={form.motherName} onChange={f('motherName')} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Mother Phone" value={form.motherPhone} onChange={f('motherPhone')} /></Grid>

            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Blood Group" value={form.bloodGroup} onChange={f('bloodGroup')} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Aadhar" value={form.aadhar} onChange={f('aadhar')} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Current Address *" value={form.currentAddress} onChange={f('currentAddress')} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Permanent Address" value={form.permanentAddress} onChange={f('permanentAddress')} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Remarks" value={form.remarks} onChange={f('remarks')} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 120 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (editMode ? 'Update' : 'Add Student')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Promote Dialog ── */}
      <Dialog open={promoteDialog} onClose={closePromoteDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#6a1b9a', borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <School sx={{ color: '#6a1b9a' }} />
            Bulk Promote — {selectedIds.length} student{selectedIds.length !== 1 ? 's' : ''}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {promoteResult ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography fontWeight={700}>{promoteResult.message}</Typography>
              </Alert>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1.5 }}>
                {[
                  { label: 'Promoted', value: promoteResult.promoted_count, color: '#2e7d32', bg: '#e8f5e9' },
                  { label: 'Skipped', value: promoteResult.skipped_count,  color: '#e65100', bg: '#fff3e0' },
                  { label: 'Errors',  value: promoteResult.error_count,    color: '#c62828', bg: '#ffebee' },
                ].map(stat => (
                  <Box key={stat.label} sx={{ p: 2, borderRadius: 2, background: stat.bg, textAlign: 'center' }}>
                    <Typography fontSize={26} fontWeight={800} color={stat.color}>{stat.value}</Typography>
                    <Typography fontSize={12} color={stat.color} fontWeight={600}>{stat.label}</Typography>
                  </Box>
                ))}
              </Box>
              {promoteResult.skipped_count > 0 && (
                <Typography fontSize={12} color="text.secondary" mt={1.5}>
                  Skipped students were either not found or already promoted to the selected class.
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              {/* <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
                This will create new student records for the selected {selectedIds.length} student{selectedIds.length !== 1 ? 's' : ''} in the target class and academic year.
              </Alert> */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField select fullWidth size="small" label="Target Academic Year *"
                    value={promoteForm.academic_year}
                    onChange={e => setPromoteForm(p => ({ ...p, academic_year: e.target.value }))}>
                    {academicYears.map(y => (
                      <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField select fullWidth size="small" label="Target Standard *"
                    value={promoteForm.standard}
                    onChange={e => setPromoteForm(p => ({ ...p, standard: e.target.value, section: '' }))}>
                    {standards.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField select fullWidth size="small" label="Target Section *"
                    value={promoteForm.section}
                    onChange={e => setPromoteForm(p => ({ ...p, section: e.target.value }))}>
                    {sections.filter(s => s.standard === promoteForm.standard).map(s => (
                      <MenuItem key={s.section} value={s.section}>{s.section}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={closePromoteDialog} variant="outlined" sx={{ borderRadius: 2 }}>
            {promoteResult ? 'Close' : 'Cancel'}
          </Button>
          {!promoteResult && (
            <Button onClick={handleBulkPromote} variant="contained" disabled={promoting}
              sx={{ background: 'linear-gradient(135deg,#6a1b9a,#8e24aa)', borderRadius: 2, fontWeight: 700, minWidth: 140 }}>
              {promoting ? <CircularProgress size={20} color="inherit" /> : `Promote ${selectedIds.length} Students`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    {/* ── ID Card Generation Dialog ── */}
      <Dialog open={idCardDialog} onClose={() => setIdCardDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge sx={{ color: '#1a237e' }} />
            Generate Student ID Cards
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Each student gets a front page (photo, details) and a back page (terms, contact info).
          </Typography>
          {academicYears.length > 0 && (
            <TextField select fullWidth size="small" label="Academic Year" value={idCardAcYear}
              onChange={e => setIdCardAcYear(e.target.value)} sx={{ mb: 3 }}>
              {academicYears.map(y => (
                <MenuItem key={y.value} value={y.value}>{y.label}{y.is_current ? ' (Current)' : ''}</MenuItem>
              ))}
            </TextField>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Section wise */}
            <Box onClick={() => openBulkIdCards('section')} sx={{
              display: 'flex', alignItems: 'center', gap: 2, p: 2.5,
              borderRadius: 2, border: '2px solid #1a237e', cursor: 'pointer',
              transition: 'all 0.2s', '&:hover': { background: '#e8eaf6' },
            }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <School sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700} color="#1a237e">Section Wise</Typography>
                <Typography fontSize={12} color="text.secondary">
                  {filterClass !== 'All' ? filterClass.replace('||', ' - ') : 'Select a class/section from filter first'}
                </Typography>
              </Box>
              {idCardLoading ? <CircularProgress size={20} sx={{ color: '#1a237e' }} /> : <Download sx={{ color: '#1a237e' }} />}
            </Box>

            {/* All students */}
            <Box onClick={() => openBulkIdCards('all')} sx={{
              display: 'flex', alignItems: 'center', gap: 2, p: 2.5,
              borderRadius: 2, border: '2px solid #2e7d32', cursor: 'pointer',
              transition: 'all 0.2s', '&:hover': { background: '#e8f5e9' },
            }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, background: 'linear-gradient(135deg,#2e7d32,#43a047)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Badge sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700} color="#2e7d32">All Students</Typography>
                <Typography fontSize={12} color="text.secondary">
                  Generate ID cards for all active students ({students.length} total)
                </Typography>
              </Box>
              {idCardLoading ? <CircularProgress size={20} sx={{ color: '#2e7d32' }} /> : <Download sx={{ color: '#2e7d32' }} />}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setIdCardDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
